import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCustomers, createCustomer, updateCustomer, deleteCustomer } from '../services/api';
import CustomerModal from '../components/CustomerModal';
import './CustomersPage.css';

function CustomersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const response = await getCustomers(search);
      setCustomers(response.customers || []);
      setError('');
    } catch (err) {
      console.error('Error loading customers:', err);
      setError(t('customers.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadCustomers();
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(customers.length / itemsPerPage));
  const paginatedCustomers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return customers.slice(start, start + itemsPerPage);
  }, [customers, currentPage, itemsPerPage]);

  const handleAddCustomer = () => {
    setSelectedCustomer(null);
    setModalOpen(true);
  };

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setModalOpen(true);
  };

  const handleViewCustomer = (customerId) => {
    navigate(`/customers/${customerId}`);
  };

  const handleDeleteCustomer = async (customerId) => {
    if (window.confirm(t('customers.deleteConfirm'))) {
      try {
        await deleteCustomer(customerId);
        loadCustomers();
      } catch (err) {
        console.error('Error deleting customer:', err);
        alert(t('customers.deleteError'));
      }
    }
  };

  const handleSaveCustomer = async (data) => {
    if (selectedCustomer) {
      await updateCustomer(selectedCustomer.customerId, data);
    } else {
      await createCustomer(data);
    }
    loadCustomers();
  };

  return (
    <div className="customers-page">
      <div className="page-header">
        <h1>{t('customers.title')}</h1>
        <button type="button" className="btn btn-primary" onClick={handleAddCustomer}>
          {t('customers.add')}
        </button>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder={t('customers.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <p className="customers-data-legend">{t('customers.dataConsentLegend')}</p>

      {error && <div className="error-message">{error}</div>}

      <div className="card">
        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : customers.length === 0 ? (
          <div className="empty-state">{t('customers.noCustomers')}</div>
        ) : (
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('customers.name')}</th>
                  <th>{t('customers.phone')}</th>
                  <th>{t('customers.email')}</th>
                  <th>{t('customers.balance')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCustomers.map((customer) => (
                  <tr key={customer.customerId}>
                    <td>{customer.name}</td>
                    <td>{customer.phone || '-'}</td>
                    <td>{customer.email || '-'}</td>
                    <td className={customer.balance < 0 ? 'negative-balance' : ''}>
                      ${(customer.balance || 0).toFixed(2)}
                    </td>
                    <td className="actions-cell">
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => handleViewCustomer(customer.customerId)}
                        title={t('common.view')}
                      >
                        👁️
                      </button>
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => handleEditCustomer(customer)}
                        title={t('common.edit')}
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        className="btn-icon btn-danger"
                        onClick={() => handleDeleteCustomer(customer.customerId)}
                        title={t('common.delete')}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && customers.length > 0 && (
          <div className="pagination-controls">
            <div className="pagination-info">
              <span>
                {t('customers.pagination.showing', {
                  from: (currentPage - 1) * itemsPerPage + 1,
                  to: Math.min(currentPage * itemsPerPage, customers.length),
                  total: customers.length,
                })}
              </span>
              <select
                className="pagination-page-size"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                aria-label={t('customers.pagination.perPage')}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            {totalPages > 1 && (
              <div className="pagination-buttons">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  «
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setCurrentPage((p) => p - 1)}
                  disabled={currentPage === 1}
                >
                  ‹
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    if (totalPages <= 7) return true;
                    if (page === 1 || page === totalPages) return true;
                    if (Math.abs(page - currentPage) <= 1) return true;
                    return false;
                  })
                  .reduce((acc, page, idx, arr) => {
                    if (idx > 0 && page - arr[idx - 1] > 1) {
                      acc.push('ellipsis-' + page);
                    }
                    acc.push(page);
                    return acc;
                  }, [])
                  .map((item) =>
                    typeof item === 'string' ? (
                      <span key={item} className="pagination-ellipsis">
                        …
                      </span>
                    ) : (
                      <button
                        type="button"
                        key={item}
                        className={`btn btn-sm ${item === currentPage ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setCurrentPage(item)}
                      >
                        {item}
                      </button>
                    )
                  )}
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={currentPage === totalPages}
                >
                  ›
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  »
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <CustomerModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveCustomer}
        customer={selectedCustomer}
      />
    </div>
  );
}

export default CustomersPage;
